import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Check, CheckSquare, Clock, Mail, Pencil, Phone,
  StickyNote, Trash2, Users, X,
} from "lucide-react";
import i18n from "../i18n";
import { Badge } from "./common";
import { formatTime } from "../utils/calendarDates";
import { typeStyle } from "../utils/calendarEvents";

const TYPE_ICONS = { call: Phone, email: Mail, meeting: Users, task: CheckSquare, note: StickyNote };
const WIDTH = 300;
const GAP = 10;
const MARGIN = 12;

/**
 * Event detail, anchored to the clicked chip.
 *
 * position: fixed rather than absolute, because both grids scroll inside
 * `overflow: auto` containers -- an absolutely positioned card would be
 * clipped by the very grid it belongs to. The tradeoff is that the anchor's
 * viewport position has to be re-read on scroll, which is what `place` does.
 *
 * On a phone it becomes a bottom sheet: 300px of card anchored to a 2mm-wide
 * chip is unusable, and the sheet matches the app's existing Modal behaviour.
 */
export default function CalendarEventPopover({
  event, anchor, isDesktop, writable, contactName,
  onClose, onToggle, onEdit, onDelete,
}) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    if (!isDesktop || !anchor || !ref.current) return;
    const a = anchor.getBoundingClientRect();
    const own = ref.current.getBoundingClientRect();
    const height = own.height || 200;

    // Right of the chip when it fits, otherwise left, otherwise pinned to the
    // right margin -- never off-screen.
    let left = a.right + GAP;
    if (left + WIDTH > window.innerWidth - MARGIN) left = a.left - WIDTH - GAP;
    if (left < MARGIN) left = Math.max(MARGIN, window.innerWidth - WIDTH - MARGIN);

    let top = a.top - 4;
    if (top + height > window.innerHeight - MARGIN) top = window.innerHeight - height - MARGIN;
    if (top < MARGIN) top = MARGIN;

    setPos({ left, top });
  }, [anchor, isDesktop]);

  useLayoutEffect(() => { place(); }, [place]);

  useEffect(() => {
    if (!isDesktop) return undefined;
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [isDesktop, place]);

  // Escape and outside-click close. Focus moves into the card on open and back
  // to the chip on close, so a keyboard user isn't dropped at the top of the
  // grid after every event they inspect.
  useEffect(() => {
    const card = ref.current;
    card?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    const onDown = (e) => {
      if (!ref.current?.contains(e.target) && !anchor?.contains(e.target)) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      if (anchor?.isConnected) anchor.focus({ preventScroll: true });
    };
  }, [anchor, onClose]);

  const Icon = TYPE_ICONS[event.type] || CheckSquare;
  const style = typeStyle(event.type);
  const when = event.allDay
    ? `${event.start.toLocaleDateString(i18n.language, { weekday: "long", day: "numeric", month: "long" })} · ${t("calendar.allDay")}`
    : `${event.start.toLocaleDateString(i18n.language, { weekday: "long", day: "numeric", month: "long" })} · ${formatTime(event.start, i18n.language)} – ${formatTime(event.end, i18n.language)}`;

  const links = [
    event.meetingId && { to: `/meetings/${event.meetingId}`, label: t("calendar.openMeeting") },
    event.activity.deal_id && { to: `/deals/${event.activity.deal_id}`, label: t("calendar.openDeal") },
    event.activity.project_id && { to: `/projects/${event.activity.project_id}`, label: t("calendar.openProject") },
    event.activity.contact_id && {
      to: `/contacts/${event.activity.contact_id}`,
      label: contactName || t("calendar.openContact"),
    },
    event.activity.company_id && { to: `/companies/${event.activity.company_id}`, label: t("calendar.openCompany") },
  ].filter(Boolean);

  const card = (
    <div
      ref={ref}
      tabIndex={-1}
      role="dialog"
      aria-label={event.subject}
      data-testid="cal-event-popover"
      style={isDesktop ? { position: "fixed", left: pos?.left ?? -9999, top: pos?.top ?? -9999, width: WIDTH } : undefined}
      className={`z-[70] bg-bg border border-border shadow-2xl outline-none
        ${isDesktop
          ? `rounded-sm ${pos ? "animate-fade-up" : "invisible"}`
          : "fixed inset-x-0 bottom-0 rounded-t-xl animate-fade-up max-h-[80vh] overflow-y-auto"}`}
    >
      <div className="flex items-start gap-2.5 px-4 pt-4 pb-3">
        <span className={`mt-0.5 w-7 h-7 rounded-sm border flex items-center justify-center shrink-0 ${style.tint}`}>
          <Icon size={14} />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`font-display font-bold leading-snug break-words ${event.completed ? "line-through text-muted" : ""}`}>
            {event.subject}
          </p>
          <p className="text-xs text-muted mt-1 flex items-center gap-1.5">
            <Clock size={12} className="shrink-0" />
            {/* first-letter, not `capitalize`: Hungarian weekday and month
                names are lowercase and want a leading capital, but `capitalize`
                title-cases every word and turns "All day" into "All Day". */}
            <span className="first-letter:uppercase">{when}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.cancel")}
          className="p-1 -mr-1 -mt-1 rounded-sm text-muted hover:bg-surface hover:text-txt outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      <div className="px-4 pb-3 flex flex-wrap gap-1.5">
        <Badge value={event.type} label={t(`statuses.${event.type}`)} />
        {event.activity.direction && (
          <Badge value={event.activity.direction} label={t(`activity.direction.${event.activity.direction}`, event.activity.direction)} />
        )}
        {event.completed && <Badge value="completed" label={t("activity.completed")} className="bg-success/15 text-success" />}
      </div>

      {event.description && (
        <p className="px-4 pb-3 text-sm text-muted whitespace-pre-wrap line-clamp-4">{event.description}</p>
      )}

      {links.length > 0 && (
        <div className="px-4 pb-3 flex flex-col gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-sm w-fit"
            >
              <ArrowUpRight size={13} className="shrink-0" />
              <span className="truncate">{l.label}</span>
            </Link>
          ))}
        </div>
      )}

      {writable && (
        <div className="border-t border-border px-2 py-2 flex items-center gap-1">
          <button
            type="button"
            data-testid="cal-popover-toggle"
            onClick={() => onToggle(event)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-sm text-xs font-medium text-txt hover:bg-surface outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors"
          >
            <Check size={13} />
            {event.completed ? t("calendar.markOpen") : t("calendar.markDone")}
          </button>
          <button
            type="button"
            data-testid="cal-popover-edit"
            onClick={() => onEdit(event)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-medium text-txt hover:bg-surface outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors"
          >
            <Pencil size={13} />
            {t("common.edit")}
          </button>
          <button
            type="button"
            data-testid="cal-popover-delete"
            onClick={() => onDelete(event)}
            aria-label={t("common.delete")}
            className="p-1.5 rounded-sm text-muted hover:bg-danger/15 hover:text-danger outline-none focus-visible:ring-2 focus-visible:ring-danger/60 transition-colors"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );

  if (isDesktop) return card;
  return (
    <div className="fixed inset-0 z-[70]">
      <button
        type="button"
        aria-label={t("common.cancel")}
        onClick={onClose}
        className="absolute inset-0 w-full bg-black/50 backdrop-blur-sm cursor-default"
      />
      {card}
    </div>
  );
}
